// Castintech dashboard — State Display standart component'leri.
// Aşama D5 (2026-05-24) — 30+ view'da tutarsız placeholder/loading/error
// pattern'ini standartlaştırır.
//
// 5 fn:
//   - createSkeleton(opts) — shimmer placeholder (loading)
//   - renderEmpty(container, opts) — boş veri durumu (icon + mesaj + CTA)
//   - renderError(container, err, opts) — hata durumu (icon + mesaj + retry)
//   - renderLoading(container, opts) — basit yükleniyor (spinner + text)
//   - clearState(container) — herhangi bir state'i temizle
//
// CSS class'lar: .ds-skeleton, .ds-empty, .ds-error, .ds-loading
// (css/dashboard.css'e tek seferlik blok olarak eklenir — bu PR'da)
//
// prefers-reduced-motion saygısı: CSS @media globalde 0.01ms transition.

export interface SkeletonOptions {
  width?: string;      // CSS width, default '100%'
  height?: string;     // CSS height, default '16px'
  rows?: number;       // multi-row skeleton, default 1
  rowGap?: string;     // satır arası boşluk, default '8px'
  rounded?: boolean;   // border-radius (true → 4px, false → 0)
}

export interface EmptyOptions {
  title?: string;      // ana mesaj, default 'Henüz veri yok'
  description?: string; // alt açıklama
  icon?: string;       // emoji veya unicode, default '📭'
  ctaLabel?: string;   // call-to-action button text
  ctaHref?: string;    // CTA URL (yoksa onClick required)
  onCta?: () => void;  // CTA click handler
}

export interface ErrorOptions {
  title?: string;      // ana mesaj, default 'Bir hata oluştu'
  description?: string; // detay (err.message gibi)
  icon?: string;       // default '⚠️'
  retryLabel?: string; // retry button text, default 'Tekrar dene'
  onRetry?: () => void;
}

export interface LoadingOptions {
  text?: string;       // default 'Yükleniyor...'
  inline?: boolean;    // satır içi mi, blok mu (default false)
}

// ─────────────────────────────────────────────────────────────────────────────
// Skeleton

/**
 * Shimmer placeholder DOM element üretir. Container'a appendChild edilebilir.
 *
 * @example
 *   const sk = createSkeleton({ rows: 3, height: '40px' });
 *   document.getElementById('list').appendChild(sk);
 */
export function createSkeleton(opts: SkeletonOptions = {}): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'ds-skeleton-wrapper';
  wrapper.setAttribute('role', 'status');
  wrapper.setAttribute('aria-label', 'Yükleniyor');
  wrapper.setAttribute('aria-busy', 'true');

  const rows = Math.max(1, opts.rows ?? 1);
  const rowGap = opts.rowGap ?? '8px';
  if (rows > 1) {
    wrapper.style.display = 'flex';
    wrapper.style.flexDirection = 'column';
    wrapper.style.gap = rowGap;
  }

  for (let i = 0; i < rows; i++) {
    const row = document.createElement('div');
    row.className = 'ds-skeleton';
    row.style.width = opts.width ?? '100%';
    row.style.height = opts.height ?? '16px';
    if (opts.rounded !== false) row.style.borderRadius = '4px';
    wrapper.appendChild(row);
  }
  return wrapper;
}

// ─────────────────────────────────────────────────────────────────────────────
// Empty state

export function renderEmpty(container: HTMLElement, opts: EmptyOptions = {}): void {
  const icon = opts.icon ?? '📭';
  const title = opts.title ?? 'Henüz veri yok';
  const description = opts.description ?? '';
  const ctaLabel = opts.ctaLabel ?? '';
  const ctaHref = opts.ctaHref ?? '';

  container.innerHTML = ''; // önceki içeriği temizle
  const el = document.createElement('div');
  el.className = 'ds-empty';
  el.setAttribute('role', 'status');

  let html = `<div class="ds-empty-icon" aria-hidden="true">${icon}</div>`;
  html += `<div class="ds-empty-title">${_escapeHtml(title)}</div>`;
  if (description) {
    html += `<div class="ds-empty-desc">${_escapeHtml(description)}</div>`;
  }
  if (ctaLabel) {
    if (ctaHref) {
      html += `<a class="ds-empty-cta" href="${_escapeAttr(ctaHref)}">${_escapeHtml(ctaLabel)}</a>`;
    } else {
      html += `<button class="ds-empty-cta" type="button">${_escapeHtml(ctaLabel)}</button>`;
    }
  }
  el.innerHTML = html;
  container.appendChild(el);

  if (opts.onCta && ctaLabel && !ctaHref) {
    const btn = el.querySelector<HTMLButtonElement>('.ds-empty-cta');
    if (btn) btn.addEventListener('click', opts.onCta);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Error state

export function renderError(
  container: HTMLElement,
  err: Error | string | null,
  opts: ErrorOptions = {},
): void {
  const icon = opts.icon ?? '⚠️';
  const title = opts.title ?? 'Bir hata oluştu';
  const description = opts.description ?? (err instanceof Error ? err.message : String(err || ''));
  const retryLabel = opts.retryLabel ?? 'Tekrar dene';

  container.innerHTML = '';
  const el = document.createElement('div');
  el.className = 'ds-error';
  el.setAttribute('role', 'alert');
  el.setAttribute('aria-live', 'assertive');

  let html = `<div class="ds-error-icon" aria-hidden="true">${icon}</div>`;
  html += `<div class="ds-error-title">${_escapeHtml(title)}</div>`;
  if (description) {
    html += `<div class="ds-error-desc">${_escapeHtml(description)}</div>`;
  }
  if (opts.onRetry) {
    html += `<button class="ds-error-retry" type="button">${_escapeHtml(retryLabel)}</button>`;
  }
  el.innerHTML = html;
  container.appendChild(el);

  if (opts.onRetry) {
    const btn = el.querySelector<HTMLButtonElement>('.ds-error-retry');
    if (btn) btn.addEventListener('click', opts.onRetry);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Loading state

export function renderLoading(container: HTMLElement, opts: LoadingOptions = {}): void {
  const text = opts.text ?? 'Yükleniyor...';
  const inline = opts.inline ?? false;

  container.innerHTML = '';
  const el = document.createElement('div');
  el.className = inline ? 'ds-loading ds-loading-inline' : 'ds-loading';
  el.setAttribute('role', 'status');
  el.setAttribute('aria-busy', 'true');
  el.setAttribute('aria-label', text);
  el.innerHTML = `<span class="ds-loading-spinner" aria-hidden="true"></span><span class="ds-loading-text">${_escapeHtml(text)}</span>`;
  container.appendChild(el);
}

// ─────────────────────────────────────────────────────────────────────────────
// Auth-required state (admin bearer gerektiren hassas paneller için)

export interface AuthRequiredOptions {
  title?: string;       // default 'Admin yetkisi gerekli'
  description?: string; // default açıklama
  icon?: string;        // default '🔒'
  ctaLabel?: string;    // default 'Admin anahtarı gir'
  onCta?: () => void;   // tipik: ensureAdminBearer + reload
}

/**
 * Hassas panellerde (api-keys, credentials-expiry gibi requireAdminBearer
 * korumalı) 401/403 yerine temiz bir "admin yetkisi gerekli" durumu gösterir.
 * .ds-empty stilini yeniden kullanır (ek CSS yok) + .ds-auth modifier.
 */
export function renderAuthRequired(container: HTMLElement, opts: AuthRequiredOptions = {}): void {
  const icon = opts.icon ?? '🔒';
  const title = opts.title ?? 'Admin yetkisi gerekli';
  const description = opts.description
    ?? 'Bu panel hassas veri içerir; görüntülemek için admin anahtarı (CASTINTECH_ADMIN_KEY) gerekir.';
  const ctaLabel = opts.ctaLabel ?? 'Admin anahtarı gir';

  container.innerHTML = '';
  const el = document.createElement('div');
  el.className = 'ds-empty ds-auth';
  el.setAttribute('role', 'status');

  let html = `<div class="ds-empty-icon" aria-hidden="true">${icon}</div>`;
  html += `<div class="ds-empty-title">${_escapeHtml(title)}</div>`;
  if (description) html += `<div class="ds-empty-desc">${_escapeHtml(description)}</div>`;
  if (ctaLabel && opts.onCta) {
    html += `<button class="ds-empty-cta" type="button">${_escapeHtml(ctaLabel)}</button>`;
  }
  el.innerHTML = html;
  container.appendChild(el);

  if (opts.onCta && ctaLabel) {
    const btn = el.querySelector<HTMLButtonElement>('.ds-empty-cta');
    if (btn) btn.addEventListener('click', opts.onCta);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Clear

export function clearState(container: HTMLElement): void {
  container.querySelectorAll('.ds-skeleton-wrapper, .ds-empty, .ds-error, .ds-loading').forEach((el) => el.remove());
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers

function _escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function _escapeAttr(s: string): string {
  return _escapeHtml(s);
}

// Default export
export default {
  createSkeleton,
  renderEmpty,
  renderError,
  renderLoading,
  renderAuthRequired,
  clearState,
};
